import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
// @ts-ignore - JS module, no types
import UserService from "../../../services/UserService";
// @ts-ignore - JS module, no types
import ProductRoleAssigner from "../../../components/common/ProductRoleAssigner";
import { parseProductRoleName, TIER_LABELS } from "../../../utils/common/productRoles";
import {
  PRC_PRODUCT_ID as PRODUCT_ID,
  isProductAdmin,
} from "../../../components/portfolio-risk-concentration/access-control";
import serverConfig from "../../../config/server";

/**
 * Product-role administration for Portfolio Risk Concentrations, mirroring
 * UnderwritingEngineV2/pages/ManageAccess.tsx and the RBPE port of it.
 *
 * Routed OUTSIDE the product's access gate on purpose: an account holding only
 * "33-admin" is denied the product surface by design (platform-api's
 * has_any_product_role counts only user/contributor/reviewer as working tiers), so if
 * this page sat behind that gate a freshly-provisioned admin would have no reachable way
 * to grant anyone -- including themselves -- a working tier. It therefore gates itself.
 */

const EYEBROW = "text-[11px] font-medium uppercase tracking-[0.16em] text-amber";
const TH =
  "px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground";

interface UserRow {
  name: string;
  email_id: string;
  roles: string[];
}

function AccessDenied() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="space-y-3 text-center">
        <div className={EYEBROW}>Restricted</div>
        <h2 className="text-lg font-semibold text-amber">Access Denied</h2>
        <p className="text-sm text-muted-foreground">
          Manage Access is restricted to Portfolio Risk Concentrations admins.
        </p>
        <Link to="/risk-concentrations" className="inline-block text-sm text-amber hover:underline">
          ← Back to Portfolio Risk Concentrations
        </Link>
      </div>
    </div>
  );
}

export default function ManageAccess() {
  const isAdmin: boolean = isProductAdmin();
  const token: string = UserService.getToken();

  const [users, setUsers] = useState<UserRow[]>([]);
  const [productRoleUsers, setProductRoleUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState("");

  // Populates the "Select user" dropdown -- every platform user, regardless of product role.
  function load() {
    axios
      .post(`${serverConfig.api_base_url}user_list_get`, { token })
      .then((res: any) => {
        if (res.data.status === 200) {
          setUsers(res.data.items || []);
        }
      });
  }

  // Populates the table below -- read live from Keycloak (product roles are never mirrored
  // into the local iam_user_role table user_list_get's `roles` comes from, so that endpoint
  // can never reflect them; see product_role_users_get.py).
  function loadProductRoleUsers() {
    setLoading(true);
    axios
      .post(`${serverConfig.api_base_url}product_role_users_get`, {
        token,
        product_id: PRODUCT_ID,
      })
      .then((res: any) => {
        if (res.data.status === 200) {
          setProductRoleUsers(res.data.items || []);
        }
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!isAdmin) return;
    load();
    loadProductRoleUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // After the hooks, never before -- an early return above them would change the hook
  // order between renders.
  if (!isAdmin) return <AccessDenied />;

  function grant(flatName: string) {
    if (!selectedEmail) {
      alert("Select a user first");
      return;
    }
    const parsed = parseProductRoleName(flatName);
    if (!parsed) return;
    axios
      .post(`${serverConfig.api_base_url}product_role_grant`, {
        token,
        email_id: selectedEmail,
        product_id: parsed.productId,
        tier: parsed.tier,
      })
      .then((res: any) => {
        if (res.data.status === 200) {
          loadProductRoleUsers();
        } else {
          // Most likely 501 "Role 33-<tier> not provisioned in Keycloak" -- the realm
          // roles have to exist before they can be granted.
          alert(res.data.message);
        }
      });
  }

  function revoke(email: string, flatName: string) {
    const parsed = parseProductRoleName(flatName);
    if (!parsed) return;
    axios
      .post(`${serverConfig.api_base_url}product_role_revoke`, {
        token,
        email_id: email,
        product_id: parsed.productId,
        tier: parsed.tier,
      })
      .then((res: any) => {
        if (res.data.status === 200) {
          loadProductRoleUsers();
        } else {
          alert(res.data.message);
        }
      });
  }

  const usersWithAccess = productRoleUsers
    .map((u) => ({
      ...u,
      productRoles: u.roles.filter((r) => parseProductRoleName(r)?.productId === PRODUCT_ID),
    }))
    .filter((u) => u.productRoles.length > 0);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-4xl px-6 py-10">
        <Link
          to="/risk-concentrations"
          className="text-xs text-muted-foreground transition-colors hover:text-amber"
        >
          ← Portfolio Risk Concentrations
        </Link>
        <div className={`mt-3 ${EYEBROW}`}>Admin · Access</div>
        <h1 className="mt-1 text-[26px] font-semibold leading-tight text-foreground">
          Manage Access
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Grant or revoke Portfolio Risk Concentrations roles User, Contributor, Reviewer,
          Admin for any user. Reviewers see and edit Manage Limits and Manage Capital;
          Contributors also approve; Admins additionally manage alerts. Admin on its own
          grants this page only — pair it with a working role to use the product.
        </p>

        <div className="mt-6 border border-border bg-card p-4">
          <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Grant a role
          </div>
          <select
            className="mb-2 mr-2 border border-[hsl(216,11%,26%)] bg-[hsl(216,11%,13%)] px-2 py-2 text-sm text-foreground"
            value={selectedEmail}
            onChange={(e) => setSelectedEmail(e.target.value)}
          >
            <option value="">Select user</option>
            {users.map((u) => (
              <option value={u.email_id} key={u.email_id}>
                {u.name} ({u.email_id})
              </option>
            ))}
          </select>
          {/* fixedProductId hides the product dropdown; value={[]} suppresses the chip
              list, leaving the Role select + Add button with the table as the record. */}
          <ProductRoleAssigner
            token={token}
            fixedProductId={PRODUCT_ID}
            value={[]}
            onAdd={grant}
            onRemove={() => {}}
          />
        </div>

        <div className="mt-6 border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="border-b border-border bg-background/40">
                <tr>
                  <th className={TH}>Name</th>
                  <th className={TH}>Email</th>
                  <th className={TH}>Portfolio Risk Concentrations Role(s)</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={3} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && usersWithAccess.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      No users have a Portfolio Risk Concentrations role yet.
                    </td>
                  </tr>
                )}
                {!loading &&
                  usersWithAccess.map((u) => (
                    <tr key={u.email_id} className="border-b border-border/50 last:border-0">
                      <td className="px-4 py-3.5 align-top text-foreground">{u.name}</td>
                      <td className="px-4 py-3.5 align-top text-foreground/80">{u.email_id}</td>
                      <td className="px-4 py-3.5 align-top">
                        <div className="flex flex-wrap items-center gap-2">
                          {u.productRoles.map((r) => {
                            const parsed = parseProductRoleName(r);
                            if (!parsed) return null;
                            return (
                              <span
                                key={r}
                                className="flex items-center gap-1.5 border border-border bg-background/60 px-2 py-1 text-xs text-foreground/85"
                              >
                                {TIER_LABELS[parsed.tier]}
                                <button
                                  type="button"
                                  className="text-muted-foreground transition-colors hover:text-destructive"
                                  onClick={() => revoke(u.email_id, r)}
                                  aria-label={`Remove ${TIER_LABELS[parsed.tier]}`}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
